import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import TopBar from "../components/dashboardParts/TopBar";
import { permissionTest } from "system/InfegyUser";
import AIGradientBox from "components/design/AI/AIGradientBox";
import AIGradientButton from "components/design/AI/AIGradientButton";
import AIGradientText from "components/design/AI/AIGradientText";
import { AssessmentOutlined, Groups2Outlined } from "@mui/icons-material";
import APIRequest, { APIError } from "system/APIRequest";
import { Query, socialDatasetId } from "system/query/Query";
import TextSkeletonLoader from "components/apiHandling/TextSkeletonLoader";
import { AIQueryResult } from "components/widgetParts/AIQueryResult";
import { AIStandaloneLoader } from "components/widgetParts/AIStandaloneLoader";
import { AIResponseType } from "system/AIResponseTypes";
import { setDocumentTitle } from "system/Utilities";

export default function AIAssistantPage(): JSX.Element {
  const { i18n } = useTranslation();

  const canUseAI = permissionTest("ai_features");

  const [prompt, setPrompt] = useState<string>('');
  const [lastPrompt, setLastPrompt] = useState<string>('');
  const [viewMode, setViewMode]= useState("summary");

  const [activeViewType, setActiveViewType] = useState<AIResponseType | undefined>(undefined);
  const [queryIsLoading, setQueryIsLoading] = useState<boolean>(false);
  const [queryResult, setQueryResult] = useState<Query | undefined>(undefined);
  const [queryErrorMessage, setQueryErrorMessage] = useState<string | undefined>(undefined);

  const [savedQueryId, setSavedQueryId] = useState<string | undefined>(undefined);

  const [showQueryInfo, setShowQueryInfo] = useState<boolean>(false);

  function runAIQuery() {
    if (prompt === lastPrompt && activeViewType === viewModeToResponseType(viewMode)) {
      return;
    }

    setQueryResult(undefined);
    setQueryIsLoading(true);
    setSavedQueryId(undefined);

    setLastPrompt(prompt);
    setActiveViewType(viewModeToResponseType(viewMode));

    APIRequest(`query/ai-assist`, {
      prompt: prompt,
    }).then((response: any) => {
      setQueryIsLoading(false);
      const newQuery = new Query({
        dataset_id : socialDatasetId,
        query: response,
        prompt: prompt,
        name: prompt
      });
      setLastPrompt(prompt);
      setQueryResult(newQuery);
    }).catch((error: APIError) => {
      setLastPrompt("");
      setQueryIsLoading(false);
      setQueryErrorMessage(error.message);
    });
  }

  function viewModeToResponseType(viewMode: string) {
    if (viewMode === "personas") {
      return AIResponseType.PERSONAS;
    }
    return AIResponseType.SUMMARY;
  }
  const viewType = viewModeToResponseType(viewMode);
  const canRun = prompt && prompt.length > 2 && (prompt !== lastPrompt || viewType !== activeViewType);

  useEffect(() => setDocumentTitle("Pulse AI"), []);

  return (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
    <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", zIndex: 100, pl:1, pr: 1 }}>
      <TopBar showLogo={true} />
    </Box>
    <Box sx={{ maxWidth:"1000px", width: "100%", margin:"20px auto", position:"relative", pl: 2, pr: 2 }}>
      {canUseAI &&
      <>
      <Paper>
        <AIGradientBox>
          <AIGradientText>
            Pulse AI
          </AIGradientText>
          <Typography variant="body1" sx={{ mb: 1, ml: 0}}>
            {i18n.t('ai-query.pulse-ai-description')}
          </Typography>
          <TextField
            autoComplete="off"
            fullWidth
            autoFocus
            placeholder={i18n.t('ai-query.ai-query-prompt-placeholder')}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              setLastPrompt("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canRun) {
                e.preventDefault();
                runAIQuery();
              }
            }} />
          <Box sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: "1fr 1fr" }}>
            <Box>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, value) => {
                  setViewMode(value)
                }}
                size="small"
              >
                <ToggleButton value="summary" aria-label="summary">
                  <AssessmentOutlined sx={{ mr: 1 }} />
                  {i18n.t('ai-query.summary-mode')}
                </ToggleButton>
                <ToggleButton value="personas" aria-label="personas">
                  <Groups2Outlined sx={{ mr: 1 }} />
                  {i18n.t('ai-query.personas-mode')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{textAlign: "right"}}>
              <AIGradientButton
                disabled={!canRun}
                onClick={() => {
                  runAIQuery();
                }}>
                {i18n.t('ai-query.run-ai-query')}
              </AIGradientButton>
            </Box>
          </Box>
        </AIGradientBox>
      </Paper>
      {(queryResult && showQueryInfo) &&
        <Paper sx={{mt: 2}}>
          <AIGradientBox>
            <AIQueryResult
              savedQueryId={savedQueryId}
              setSavedQueryId={setSavedQueryId}
              queryResult={queryResult}
              />
          </AIGradientBox>
        </Paper>
      }
      {(queryResult || queryIsLoading || queryErrorMessage) &&
        <Box sx={{display:"grid", gridTemplateColumns:"1fr 1fr", mt: 2}}>
          <Typography variant="h3">
            {activeViewType === AIResponseType.SUMMARY &&
              <>{i18n.t("ai-query.summary-mode")}</>
            }
            {activeViewType === AIResponseType.PERSONAS &&
              <>{i18n.t("ai-query.personas-mode")}</>
            }
          </Typography>
          {queryResult &&
            <Box sx={{textAlign: "right"}}>
              <Button
                  variant="text"
                  onClick={() => setShowQueryInfo(!showQueryInfo)}>
                {showQueryInfo ? i18n.t("ai-query.hide-query-info") : i18n.t("ai-query.show-query-info")}
              </Button>
            </Box>
          }
        </Box>
      }
      {(queryResult || queryIsLoading || queryErrorMessage) &&
        <Paper>
          <AIGradientBox>
            {queryErrorMessage &&
              <Alert severity="error" sx={{mt: 2}}>
                {queryErrorMessage}
              </Alert>
            }
            {queryIsLoading &&
              <TextSkeletonLoader />
            }
            {(queryResult && !queryErrorMessage && !queryIsLoading && activeViewType) &&
              <AIStandaloneLoader
                queryBody={queryResult}
                responseType={activeViewType}
                showRerunButton={false}
                autoLoad={true}
                />
            }
          </AIGradientBox>
        </Paper>
        }
      </>
      }
      {!canUseAI &&
        <Alert severity="warning" sx={{m: 2}}>
          {i18n.t("ai-query.not-allowed")}
        </Alert>
      }
    </Box>
  </Box>
  );
}